import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { authService } from '../../services/authService';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const currentYear = new Date()?.getFullYear();

  const validatePassword = (password) => {
    if (password?.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const newErrors = {};
    
    const passwordError = validatePassword(formData?.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }
    
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await authService?.updatePassword(formData?.password);

      if (error) {
        setErrors({
          submit: error?.message || 'Failed to update password. Please try again.'
        });
        setIsLoading(false);
        return;
      }

      // Success - redirect to login
      navigate('/login', { 
        state: { message: 'Password updated successfully! Please sign in with your new password.' }
      });
    } catch (err) {
      setErrors({
        submit: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Reset Password - HungryElph | Set New Password</title>
        <meta 
          name="description" 
          content="Set a new password for your HungryElph account." 
        />
      </Helmet>

      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-2xl shadow-warm-lg p-6 md:p-8 lg:p-10">
            <div className="flex items-center justify-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="KeyRound" size={32} color="var(--color-primary)" />
              </div>
            </div>

            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                Set New Password
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Enter your new password below
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="New Password"
                  placeholder="Enter new password"
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  error={errors?.password}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] p-1 hover:bg-muted rounded transition-smooth"
                >
                  <Icon 
                    name={showPassword ? 'EyeOff' : 'Eye'} 
                    size={18} 
                    color="var(--color-muted-foreground)" 
                  />
                </button>
              </div>

              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm New Password"
                  placeholder="Confirm new password"
                  value={formData?.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                  error={errors?.confirmPassword}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-[38px] p-1 hover:bg-muted rounded transition-smooth"
                >
                  <Icon 
                    name={showConfirmPassword ? 'EyeOff' : 'Eye'} 
                    size={18} 
                    color="var(--color-muted-foreground)" 
                  />
                </button>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Password requirements:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={14} color="var(--color-muted-foreground)" />
                    At least 8 characters long
                  </li>
                </ul>
              </div>

              {errors?.submit && (
                <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                  <p className="text-sm text-destructive flex-1">{errors?.submit}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={isLoading}
                iconName="Check"
                iconPosition="right"
              >
                Update Password
              </Button>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-smooth"
              >
                <Icon name="ArrowLeft" size={16} />
                Back to Sign In
              </button>
            </form>
          </div>

          <footer className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              &copy; {currentYear} HungryElph. All rights reserved.
            </p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;